import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Keyboard } from '@mui/icons-material';

interface ShortcutsHelpModalProps {
  open: boolean;
  onClose: () => void;
}

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

const ShortcutsHelpModal: React.FC<ShortcutsHelpModalProps> = ({ open, onClose }) => {

  const shortcuts: Shortcut[] = [
    // General shortcuts
    { keys: ['Ctrl', 'K'], description: 'Open command palette', category: 'General' },
    { keys: ['Ctrl', 'Shift', 'P'], description: 'Open command palette', category: 'General' },
    { keys: ['/'], description: 'Quick search / command palette', category: 'General' },
    { keys: ['?'], description: 'Show this help dialog', category: 'General' },

    // Navigation shortcuts
    { keys: ['Ctrl', 'D'], description: 'Go to Dashboard', category: 'Navigation' },
    { keys: ['Ctrl', 'C'], description: 'Go to Conversations', category: 'Navigation' },
    { keys: ['Ctrl', 'P'], description: 'Go to Contacts', category: 'Navigation' },
    { keys: ['Ctrl', 'R'], description: 'Go to Reports', category: 'Navigation' },
    { keys: ['Ctrl', 'T'], description: 'Go to Teams', category: 'Navigation' },
    { keys: ['Ctrl', 'L'], description: 'Go to Labels', category: 'Navigation' },
    { keys: ['Ctrl', 'Shift', 'S'], description: 'Go to Settings', category: 'Navigation' },
    { keys: ['Ctrl', 'N'], description: 'New conversation', category: 'Navigation' },

    // Quick navigation with 'g'
    { keys: ['G', 'D'], description: 'Go to Dashboard', category: 'Quick Navigation' },
    { keys: ['G', 'C'], description: 'Go to Conversations', category: 'Quick Navigation' },
    { keys: ['G', 'P'], description: 'Go to Contacts', category: 'Quick Navigation' },
    { keys: ['G', 'R'], description: 'Go to Reports', category: 'Quick Navigation' },
    { keys: ['G', 'T'], description: 'Go to Teams', category: 'Quick Navigation' },
    { keys: ['G', 'L'], description: 'Go to Labels', category: 'Quick Navigation' },
    { keys: ['G', 'S'], description: 'Go to Settings', category: 'Quick Navigation' },

    // Conversation shortcuts
    { keys: ['R'], description: 'Focus reply box', category: 'Conversations' },
    { keys: ['A'], description: 'Assign conversation', category: 'Conversations' },
    { keys: ['C'], description: 'Close/resolve conversation', category: 'Conversations' },
    { keys: ['S'], description: 'Snooze conversation', category: 'Conversations' },
    { keys: ['P'], description: 'Set priority', category: 'Conversations' },
    { keys: ['L'], description: 'Add label', category: 'Conversations' },
    { keys: ['Ctrl', 'Enter'], description: 'Send message', category: 'Conversations' },
    { keys: ['Ctrl', 'Shift', 'R'], description: 'Resolve conversation', category: 'Conversations' },
    { keys: ['Ctrl', 'E'], description: 'Edit last message', category: 'Conversations' },
  ];

  const groupedShortcuts = shortcuts.reduce((groups, shortcut) => {
    if (!groups[shortcut.category]) {
      groups[shortcut.category] = [];
    }
    groups[shortcut.category].push(shortcut);
    return groups;
  }, {} as Record<string, Shortcut[]>);

  const renderShortcutKeys = (keys: string[]) => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {keys.map((key, index) => (
        <React.Fragment key={key}>
          <Chip
            label={key}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: 24,
              backgroundColor: 'hsl(var(--muted))',
              borderColor: 'hsl(var(--border))',
              color: 'hsl(var(--foreground))',
              fontFamily: 'monospace',
            }}
          />
          {index < keys.length - 1 && (
            <Typography variant="body2" sx={{ mx: 0.5, color: 'text.secondary' }}>
              +
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Keyboard />
        <Typography variant="h6">
          Keyboard Shortcuts
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Use these keyboard shortcuts to navigate and interact with Converse more efficiently.
        </Typography>

        <Grid container spacing={3}>
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <Grid item xs={12} md={6} key={category}>
              <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                {category}
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {categoryShortcuts.map((shortcut, index) => (
                  <React.Fragment key={`${category}-${index}`}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            gap: 2
                          }}>
                            <Typography variant="body2">
                              {shortcut.description}
                            </Typography>
                            {renderShortcutKeys(shortcut.keys)}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < categoryShortcuts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ 
          mt: 3, 
          p: 2, 
          backgroundColor: 'hsl(var(--muted) / 0.5)', 
          borderRadius: 1,
          border: 1,
          borderColor: 'hsl(var(--border))'
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            💡 Pro Tips:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Most shortcuts work globally, except conversation-specific ones that only work when viewing a conversation<br/>
            • Press <strong>G</strong> followed by another key for quick navigation<br/>
            • Use <strong>Ctrl+K</strong> or <strong>/</strong> to quickly find and execute any action<br/>
            • Shortcuts are disabled when typing in input fields
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShortcutsHelpModal;